import CfdChart, { TravelTimeSeries } from './CfdChart';
import { Button, Col, Divider, Popover, Row, Space } from 'antd';
import { ColorResult, CompactPicker } from 'react-color';
import DateRangeSelector from '../DateRange/DateRangeSelector';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { DashboardDateRangeId, DayOfWeek, Slice } from '../../util/types';
import * as _ from 'lodash';
import { DashboardDateRange } from '../../util/DashboardDateRange';
import { createData, getSliceName } from './CfdUtil';
import * as Papa from 'papaparse';
import { toast } from 'react-toastify';
import { TRAJECTORY_CRUNCHER_URL } from '../util/config';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import download from '../../util/download';
import DayOfWeekSelector from '../DayOfWeekSelector';
import TimeOfDaySlider from '../TimeOfDaySlider';
import { dayjsAsMinutes, DAYS_OF_WEEK } from '../../util/dates';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCurrentWidth } from 'react-socks';
import Logos from '../Logos';

type CfdContainerProps = {
  routeId: number;
  organizationName?: string;
};

interface TravelTimeData {
  travelTime: number;
  unixTime: number;
}

dayjs.extend(utc);

const DEFAULT_SELECTED_DAYS_OF_WEEK: DayOfWeek[] = DAYS_OF_WEEK;
// const DEFAULT_TIMES: [number, number] = [timeAsMinutes('08:00'), timeAsMinutes('17:00')];
const DEFAULT_COLORS: string[] = ['#ff0000', '#32cd32', '#FB9E00', '#0062B1', '#FA28FF'];

const defaultDateRanges: DashboardDateRange[] = [27, 9]
  .map(dateRangeId => new DashboardDateRange(dateRangeId as DashboardDateRangeId));

const CfdContainer = (props: CfdContainerProps) => {
  const params = useParams<{ routeId?: string, dateRangeId?: string }>();
  // Use the dateRangeId from the URL if it's there
  if (params.dateRangeId) defaultDateRanges[0] = new DashboardDateRange(parseInt(params.dateRangeId, 10) as DashboardDateRangeId);

  // The number of date ranges to show
  // const [numDateRanges, setNumDateRanges] = useState<number>(2);
  // To prevent unnecessary calls to the backend to get date ranges
  const [allDateRanges, setAllDateRanges] = useState<DashboardDateRange[]>([]);

  // The currently selected date ranges
  const [selectedDateRanges, setSelectedDateRanges] = useState<DashboardDateRange[]>(defaultDateRanges);
  // The currently selected colors
  const [selectedColors, setSelectedColors] = useState<string[]>(DEFAULT_COLORS);

  // The travel time data that gets plotted
  const [travelTimeData, setTravelTimeData] = useState<TravelTimeData[][]>([]);

  // UI stuff
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getDataDisabled, setGetDataDisabled] = useState<boolean>(true);

  // These are the values for what's currently selected
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>(DEFAULT_SELECTED_DAYS_OF_WEEK);
  const [timesOfDay, setTimesOfDay] = useState<[number, number] | null>();

  // Slices get created based on daysOfWeek and timesOfDay
  const [slices, setSlices] = useState<Slice[]>([]);

  const dateRangesRef = useRef<DashboardDateRange[]>(selectedDateRanges);
  const travelTimeDataRef = useRef<TravelTimeData[][]>(travelTimeData);

  const width = useCurrentWidth();

  // Decide the style of the CFD charts based on screen size
  const CFD_CHART_STYLE: CSSProperties = { width: '460px', height: '500px', padding: '1em' };

  const updateColor = (color: string, index: number) => setSelectedColors(prevState => [
    ...prevState.slice(0, index),
    color,
    ...prevState.slice(index + 1),
  ]);

  const submitClickedListener = () => {
    if (_.uniq(selectedDateRanges.map(d => d && d.dateRangeId)).length !== selectedDateRanges.length) {
      toast.error('Remove duplicate date ranges.');
      return;
    }
    // If none of the date ranges are falsy
    if (selectedDateRanges.every(Boolean)) {
      setIsLoading(true);
      const promises = [];
      for (const dateRange of selectedDateRanges) {
        const requestData = {
          route: JSON.parse(sessionStorage.getItem('route') as string),
          startDateTime: dateRange.parsedStartDate as string,
          endDateTime: dateRange.parsedEndDate as string,
          // TODO Un-hardcode this
          roadWidth: 150,
          movementTable: JSON.parse(sessionStorage.getItem('movementTable') as string),
          fields: ['unixTime', 'travelTime'],
        };
        promises.push(
          axios({
            url: `${TRAJECTORY_CRUNCHER_URL}get_wejo_travel_times`,
            method: 'POST',
            headers: { 'Authorization': `Bearer ${sessionStorage.getItem('sessionId')}` },
            data: requestData,
          }),
        );
      }
      // Group all the promises and run .then() when they all come back
      Promise
        .all(promises)
        .then((res) => {
          setGetDataDisabled(true);
          const data = res.map(d => d.data);

          // Parse the data
          const parsed = data.map(d => {
            const parseResult = Papa.parse(d, {
              header: true,
              skipEmptyLines: true,
            }).data as [{ travelTime: string, unixTime: string }];

            // seconds to minutes
            return parseResult
              .map(p => ({
                travelTime: parseInt(p.travelTime) / 60,
                unixTime: parseFloat(p.unixTime),
              }));
          }) as TravelTimeData[][];
          setTravelTimeData(parsed);

          setIsLoading(false);
        });
    }
  };

  const createSliceButtonClicked = () => {
    if (!timesOfDay) {
      toast.error('Times of day not set!');
      return;
    }
    if (!daysOfWeek) {
      toast.error('Days of week not set!');
      return;
    }
    setSlices([...slices, { days: daysOfWeek, times: timesOfDay }]);
  };

  const createSeries = (travelTimes: number[], name: string, color: string, idx?: number): TravelTimeSeries => {
    return {
      data: createData(travelTimes),
      type: 'line',
      name,
      symbol: 'none',
      itemStyle: {
        color,
      },
      lineStyle: {
        color,
      },
      animationDuration: 1500,
      animationEasing: 'quadraticOut',
      ...(idx !== undefined && { animationDelay: idx * 250 }),
    };
  };

  const createSliceSeries = (data: TravelTimeData[], name: string, color: string, slice: Slice, idx?: number): TravelTimeSeries => {
    const dataWithDates: (TravelTimeData & { parsed: Dayjs })[] = data.map(d => ({
      ...d,
      parsed: dayjs.unix(d.unixTime),
    }));
    let relevantData = dataWithDates
      .filter(d =>
        slice.days
          .map(dow => dow.value)
          .includes(d.parsed.local().day()),
      );
    relevantData = relevantData.filter(d => {
      let filter = false;
      if (slice.times[0] < slice.times[1]) {
        // Normal
        filter = dayjsAsMinutes(d.parsed.local()) > slice.times[0] && dayjsAsMinutes(d.parsed.local()) < slice.times[1];
      } else {
        // Exclude selected
        filter = dayjsAsMinutes(d.parsed.local()) > slice.times[0] || dayjsAsMinutes(d.parsed.local()) < slice.times[1];
      }
      return filter;
    });

    return createSeries(relevantData.map(d => d.travelTime), name, color, idx);
  };

  useEffect(() => {
    travelTimeDataRef.current = travelTimeData;
  }, [travelTimeData]);

  useEffect(() => {
    dateRangesRef.current = selectedDateRanges;
    // All the date range id's are 0
    if (selectedDateRanges.filter(Boolean).map(d => d.dateRangeId).filter(Boolean).length === 0) return;
    if (_.uniq(selectedDateRanges.map(d => d && d.dateRangeId),
    ).length !== selectedDateRanges.length) {
      toast.warn('Duplicate date ranges.');
    }
  }, [selectedDateRanges]);

  // useEffect(() => {
  //   const incompleteSlices = slices
  //     .filter((slice) => travelTimeData
  //       .map(data => createSliceSeries(data, '', '', slice))
  //       .filter(series => !series.data.length));
  // }, [travelTimeData, slices]);

  // Set the Get Data button to disabled if any of the date ranges aren't filled in
  useEffect(() => {
    setGetDataDisabled(
      selectedDateRanges.filter(dateRange => {
        return dateRange && dateRange.parsedStartDate && dateRange.parsedEndDate;
      }).length !== selectedDateRanges.length);
  }, [selectedDateRanges]);

  useHotkeys('d', () => {
    // Filter out all series' that don't have any data
    if (travelTimeDataRef.current.filter(value => value.length).length < 1) {
      toast.error('No travel times loaded yet, please press render and wait for them to load.', { autoClose: 5000 });
      return;
    }
    travelTimeDataRef.current.forEach(((value, index) => {
      if (value.length) {
        // Make an array of objects with travelTime set to the travelTime of each data point
        const csvData = Papa.unparse(value.map(d => ({
          capturedTimestamp: dayjs.unix(d.unixTime).format('YYYY-MM-DD HH:mm:ss'),
          travelTime: d.travelTime * 60,
        })));
        // Download it
        download(`${dateRangesRef.current[index].getDateHelperDisplayName().split(/\s/).join('_')}.csv`, csvData);
      }
    }));
  });

  const onCfdChartDelete = (index: number) => {
    setSlices(prevState => prevState.filter((v, i) => i !== index));
  };

  const onDateRangeSelectorChange = (changedDateRange: DashboardDateRange, index?: number) => {
    if (index === null || index === undefined) return;
    // Use the previous state to create the new state.
    // The [...arr.slice(0, idx), item, ...arr.slice(idx + 1)] syntax inserts item at idx without mutating.
    setSelectedDateRanges(prevState => {
      const newThing = [
        ...prevState.slice(0, index),
        changedDateRange,
        ...prevState.slice(index + 1),
      ];
      return newThing;
    });
  };

  return (
    <Row style={{ width: '100vw', height: '100vh' }}>
      <Col span={18} style={{}}>
        <Space wrap={true} style={{ width: '100%', height: '100vh', alignItems: 'flex-start', overflow: 'auto' }}>
          <CfdChart
            style={CFD_CHART_STYLE}
            travelTimeSeriesList={
              travelTimeData
                .map((d, idx) => createSeries(
                  d.map(dataPoint => dataPoint.travelTime),
                  selectedDateRanges[idx].name || selectedDateRanges[idx].getDateHelperDisplayName(),
                  selectedColors[idx], idx,
                ))
            }
            maxTravelTimeAllData={
              travelTimeData.reduce((acc: number, ttData) => (
                Math.max(...ttData.map(d => d.travelTime))), 0)
            }
            title={'All data'}/>
          {slices.map((slice, index) =>
            <CfdChart
              key={getSliceName(slice)}
              index={index}
              style={CFD_CHART_STYLE}
              onDelete={onCfdChartDelete}
              maxTravelTimeAllData={
                travelTimeData.reduce((acc: number, ttData) => (
                  Math.max(...ttData.map(d => d.travelTime))), 0)
              }
              travelTimeSeriesList={
                travelTimeData.map((d, idx) => createSliceSeries(
                  d,
                  selectedDateRanges[idx].name || selectedDateRanges[idx].getDateHelperDisplayName(),
                  selectedColors[idx],
                  slice,
                  idx,
                ))
              }
              title={getSliceName(slice)}
            />,
          )}
        </Space>
      </Col>
      <Col span={6}>
        <div
          style={{ display: 'flex', height: '100%', justifyContent: 'space-between', flexDirection: 'column' }}>
          <Space direction={'vertical'}
                 style={{ padding: '1em', width: '100%', alignItems: 'space-between' }}>
            <Space
              direction={'horizontal'}
              style={{ alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}
              wrap={true}>
              <Space direction={'vertical'} style={{ alignItems: 'flex-start' }}>
                {
                  selectedDateRanges.map((dateRange, index) =>
                    <Space key={index * 1000}>
                      <DateRangeSelector
                        style={{ width: '200px' }}
                        allDateRanges={allDateRanges}
                        onNewDateRanges={setAllDateRanges}
                        routeId={props.routeId}
                        organizationName={JSON.parse(sessionStorage.getItem('organizationName') as string)}
                        key={index}
                        index={index}
                        dateRangeId={(dateRange && dateRange.dateRangeId) ? dateRange.dateRangeId : 0 as DashboardDateRangeId}
                        maxDateRange={90}
                        onChange={onDateRangeSelectorChange}
                      />

                      <Popover trigger={'click'} placement={'bottomLeft'} style={{ padding: '0' }}
                               content={
                                 <CompactPicker
                                   color={selectedColors[index]}
                                   onChangeComplete={(color: ColorResult) => updateColor(color.hex, index)}
                                 />
                               }>
                        <Button style={{ backgroundColor: selectedColors[index] }}/>
                      </Popover>
                      {/*{index > 1 &&*/}
                      {/*<Button icon={<CloseOutlined/>} shape={"circle"} onClick={() => {*/}
                      {/*    setSelectedDateRanges([...selectedDateRanges.slice(0, index), ...selectedDateRanges.slice(index + 1)])*/}
                      {/*}}/>*/}
                      {/*}*/}
                    </Space>,
                  )
                }
                <Space direction={'horizontal'}>
                  <Button
                    onClick={() => {
                      setSelectedDateRanges([
                        ...selectedDateRanges,
                        new DashboardDateRange(1 as DashboardDateRangeId),
                      ]);
                    }}
                    disabled={!!selectedDateRanges.filter(d => !d).length || selectedDateRanges.length >= 5}
                  >Add</Button>
                  <Button disabled={selectedDateRanges.length <= 2}
                          onClick={() => {
                            // setNumDateRanges(numDateRanges - 1);
                            if (travelTimeData.length === selectedDateRanges.length) {
                              setTravelTimeData(travelTimeData.slice(0, -1));
                            }
                            setSelectedDateRanges(selectedDateRanges.slice(0, -1));
                          }}
                  >Remove</Button>
                </Space>
              </Space>
              <Button
                disabled={getDataDisabled}
                type={'primary'}
                loading={isLoading}
                onClick={submitClickedListener}
                style={{ marginRight: (width < 1550 ? '50px' : '0') }}
              >Get Data</Button>
            </Space>
            <Divider/>
            {/*<Col style={{justifyContent: 'space-between'}}>*/}
            <DayOfWeekSelector
              style={{ marginBottom: '1em' }}
              value={daysOfWeek}
              onChange={setDaysOfWeek}
              disabled={travelTimeData.length !== selectedDateRanges.length}
            />
            <TimeOfDaySlider
              style={{ marginLeft: '1.5em' }}
              onChange={setTimesOfDay}
              disabled={travelTimeData.length !== selectedDateRanges.length}
            />
            <Button
              disabled={!daysOfWeek.length || !timesOfDay || travelTimeData.length !== selectedDateRanges.length}
              type={'primary'}
              onClick={createSliceButtonClicked}
            >Create Slice</Button>
            {/*</Col>*/}
          </Space>
          <Logos
            style={{
              marginTop: '10px',
              display: 'flex',
              position: 'relative',
              bottom: 0,
              marginRight: '8px',
              justifySelf: 'flex-end',
              maxHeight: '150px',
            }}/>
        </div>
        {/*<CfdChartDataRetriever style={{height: '500px', width: '500px'}} dateRanges={dateRanges}/>*/}
      </Col>
    </Row>
  );
};

export default CfdContainer;
